// Lista de empleados

import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from '@mui/icons-material/Download';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EmployeeUserWizardForm from './EmployeeUserWizardForm';
import { getUserTypeLabel } from './UserType';
import { apiFetch, apiFetchJson } from '../fetchWrapper';
import GIconButton from './GIconButton';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);

    apiFetchJson('/api/employees')
      .then((data) => {
        if (!data) return;
        setEmployees(data || []);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to load employees', err);
        setError(err.message || 'Failed to load');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div>Loading employees…</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;
  if (!employees.length) return <div>No se encontraron empleados.</div>;

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }
    try {
      const res = await apiFetch(`/api/employees/${id}`, {
        method: 'DELETE',
      });
      if (!res) return;
      if (!res.ok) {
        throw new Error('Error al eliminar el usuario');
      }
      setEmployees(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al eliminar el usuario');
    }
  };
  
  const handleAdd = async (id) => {
    setEditingEmployee(null);
    setShowForm(true);
  }

  const handleCancel = () => {
    setShowForm(false);
    setEditingEmployee(null);
  }

  const handleSave = async (form) => {
    setError(null);
    const payload = {
      id: editingEmployee?.id || 0,
      active: form.active ?? true,
      rfc: form.rfc,
      curp: form.curp,
      salary: form.salary,
      user: {
        email: form.email,
        password: form.password,
        role: form.role,
        person: {
          id: form.personId || 0,
          firstname: form.firstname,
          lastnameFirst: form.lastnameFirst,
          lastnameSecond: form.lastnameSecond,
          email: form.email,
          maritalStatus: form.maritalStatus,
          birthDate: form.birthDate,
          phoneNumber: form.phoneNumber,
          mobileNumber: form.mobileNumber,
        },
      },
      address: {
        id: form.addressId || 0,
        street: form.street,
        neighborhood: form.neighborhood,
        municipality: form.municipality,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        country: form.country || 'Mexico',
      },
      contactPerson: {
        id: form.contactPerson?.id || 0,
        firstname: form.contactPerson?.firstname,
        lastnameFirst: form.contactPerson?.lastnameFirst,
        lastnameSecond: form.contactPerson?.lastnameSecond,
        email: form.contactPerson?.email,
        phoneNumber: form.contactPerson?.phoneNumber,
        mobileNumber: form.contactPerson?.mobileNumber,
      },
    };
    try {
      const method = editingEmployee ? 'PUT' : 'POST';
      const url = editingEmployee ? `/api/employees/${editingEmployee.id}` : `/api/employees`;
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const savedEmployee = await res.json();
      setEmployees(prev => {
        const exists = prev.some(e => e.id === savedEmployee.id);
        return exists
          ? prev.map(e => (e.id === savedEmployee.id ? savedEmployee : e))
          : [...prev, savedEmployee];
      });

      setShowForm(false);

    } catch (err) {
      console.error('Failed to save employee', err);
      setError(err.message || 'Failed to save employee');
    }
  };

  const handleEdit = async (employee) => {
    setEditingEmployee(employee);
    setShowForm(true);
  }

  const handleUnlock = async (employeeId) => {
    try {
      const res = await apiFetch(`/api/users/${employeeId}/reset-failed-logins`, { method: 'POST' });
      setEmployees((prev) =>
        prev.map((e) =>
          e.id === employeeId
            ? {
              ...e,
              user: {
                ...e.user,
                failedLogins: 0,
              },
            }
            : e
        )
      );

    } catch (error) {
      console.error("Failed to reset failed logins:", error);
    }
  }

  const handleExport = async () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Listado de Empleados", 14, 22);

    // Preparar datos para la tabla
    const tableColumn = ["Nombre", "Email", "Teléfono", "Rol"];
    const tableRows = filteredEmployees.map((u) => [
      `${u.user?.person?.firstname || ''} ${u.user?.person?.lastnameMaternal || ''} ${u.user?.person?.lastnamePaternal || ''}`,
      u.user?.person?.email || '-',
      u.user?.person?.phoneNumber || '-',
      u.user?.role || '-',
    ]);

    autoTable(doc, {
      startY: 30, // start below title
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10 },
    });

    // guardar PDF
    doc.save("empleados.pdf");
  }

  const handleExportCsv = () => {
    const csvHeaders = [
      'Id',
      'Nombre',
      'Email',
      'Teléfono',
      'Rol',
      'Accesos fallidos',
    ];

    const csvRows = filteredEmployees.map((u) => {
      const fullName = `${u.user?.person?.firstname || ''} ${u.user?.person?.lastnameFirst || ''} ${u.user?.person?.lastnameSecond || ''}`.trim();
      return [
        u.id,
        fullName,
        u.user?.person?.email || '-',
        u.user?.person?.phoneNumber || '-',
        u.user?.role || '-',
        u.user?.failedLogins ?? 0,
      ];
    });

    const csvContent = [csvHeaders, ...csvRows]
      .map((row) => row
        .map((value) => `"${String(value || '').replace(/"/g, '""')}"`)
        .join(','))
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'empleados.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const filteredEmployees = employees.filter(u => {
    const fullName = `${u.user?.person?.firstname || ''} ${u.user?.person?.lastnameMaternal || ''} ${u.user?.person?.lastnamePaternal || ''}`.toLowerCase();

    return (
      fullName.includes(search.toLowerCase()) ||
      u.user?.person?.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.user?.role?.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <>
      {showForm && (
        <>
          <EmployeeUserWizardForm
            employee={editingEmployee}
            onCancel={handleCancel}
            onSave={handleSave}
          />
          <br />
        </>
      )}

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        mr: 2
      }}>
        <Typography variant="h6">Lista de usuarios</Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
        }}>
          <Button sx={{
            backgroundColor: "#3f74b1",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#364f6b"
            }
          }}
            startIcon={<AddIcon />}
            onClick={handleAdd}>
            Agregar usuario
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}>
            Exportar PDF
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}>
            Exportar CSV
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          p: 2,
          width: '100%',
          overflowX: 'auto'
        }}>
        <TextField
          label="Buscar usuario"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table sx={{ minWidth: 650 }}>
          <TableHead
            sx={{
              backgroundColor: '#f5f5f5',
              textTransform: 'uppercase',
            }}>

            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Id</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Teléfono</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Rol</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Accesos fallidos</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredEmployees.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No se encontraron resultados
                </TableCell>
              </TableRow>
            )}
            {filteredEmployees.map((u) => (
              <TableRow key={u.id}
                hover
                sx={{
                  "&:hover .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                  cursor: "pointer"
                }}
              >
                <TableCell>{u.id}</TableCell>
                <TableCell>{u.user?.person?.firstname || ''} {u.user?.person?.lastnameFirst || ''} {u.user?.person?.lastnameSecond || ''}</TableCell>
                <TableCell>{u.user?.person?.email || '-'}</TableCell>
                <TableCell>{u.user?.person?.phoneNumber || '-'}</TableCell>
                <TableCell>{getUserTypeLabel(u.user?.role || '-')}</TableCell>
                <TableCell>{u.user?.failedLogins}</TableCell>
                <TableCell>
                  <GIconButton tooltip="Editar" color="primary" onClick={() => handleEdit(u)}><EditIcon /></GIconButton>
                  <GIconButton disabled={(u.user?.failedLogins ?? 0) === 0} tooltip="Desbloquear cuenta" onClick={() => handleUnlock(u.id)}><LockOpenIcon /></GIconButton>
                  <GIconButton tooltip="Borrar" color="error" onClick={() => handleDelete(u.id)}><DeleteIcon /></GIconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
